
import { useCallback } from 'react';

interface HeroSectionProps {
  onNavigate: (sectionId: string) => void;
}

export default function HeroSection({ onNavigate }: HeroSectionProps) {
  const handleNavigate = useCallback((sectionId: string) => {
    if (typeof onNavigate === 'function') {
      try {
        onNavigate(sectionId);
      } catch (error) {
        console.error(`Navigation to "${sectionId}" failed:`, error);
      }
    }
  }, [onNavigate]);

  return (
    <section
      id="home"
      className="relative h-screen min-h-[700px] md:min-h-[800px] overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0aae4d581e856b502a9bfc4536265363.jpg"
          alt="Architettura sostenibile moderna"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center max-w-[1400px] mx-auto px-5 md:px-8 w-full">
        <div className="max-w-[800px]">
          <div className="flex items-center gap-3 mb-5 md:mb-8">
            <div className="w-8 md:w-12 h-[1px] bg-[#6b8e7f]"></div>
            <span className="font-montserrat text-[10px] md:text-[11px] uppercase tracking-[3px] md:tracking-[4px] text-[#6b8e7f]">
              Architettura Ecologica d&apos;Avanguardia
            </span>
          </div>

          <h1 className="font-playfair text-white leading-[1.1]">
            <span className="block text-[36px] sm:text-[48px] md:text-[68px] font-bold">
              L&apos;Armonia tra
            </span>
            <span className="block text-[42px] sm:text-[56px] md:text-[82px] font-bold mt-1">
              Pietra e{' '}
              <em className="text-[#8fb5a3] not-italic">Respiro</em>
            </span>
          </h1>

          <p className="font-montserrat text-[14px] md:text-[17px] text-white/70 mt-5 md:mt-8 max-w-[520px] leading-[1.8] md:leading-[1.9]">
            Progettare per il futuro, onorando la terra. Ogni edificio è un
            organismo vivente che respira in simbiosi con il suo ambiente
            naturale.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 mt-8 md:mt-12">
            <button
              onClick={() => handleNavigate('progetti')}
              className="font-montserrat text-[12px] md:text-[13px] uppercase tracking-[2px] bg-[#6b8e7f] text-white px-6 md:px-8 py-3.5 md:py-4 rounded-lg hover:bg-[#5a7d6e] transition-all duration-400 cursor-pointer whitespace-nowrap"
            >
              Scopri i Progetti
            </button>
            <button
              onClick={() => handleNavigate('storia')}
              className="font-montserrat text-[12px] md:text-[13px] uppercase tracking-[2px] text-white/80 border border-white/30 px-6 md:px-8 py-3.5 md:py-4 rounded-lg hover:bg-white/10 hover:border-white/50 transition-all duration-400 cursor-pointer whitespace-nowrap"
            >
              La Mia Storia
            </button>
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/30 backdrop-blur-md border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0">
            {[
              { value: '30+', label: 'Anni di Esperienza' },
              { value: '150+', label: 'Progetti Realizzati' },
              { value: '67%', label: 'Riduzione CO\u2082 Media' },
              { value: 'A+++', label: 'Classe Energetica Standard' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3 md:gap-4">
                {i > 0 && (
                  <div className="hidden md:block w-[1px] h-10 bg-white/15 -ml-4 mr-0"></div>
                )}
                <div>
                  <span className="block font-playfair text-[22px] md:text-[28px] font-bold text-white">
                    {stat.value}
                  </span>
                  <span className="block font-montserrat text-[9px] md:text-[11px] text-white/50 uppercase tracking-[1px]">
                    {stat.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-28 md:bottom-28 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce z-10 hidden md:flex">
        <div className="w-[1px] h-8 bg-white/30"></div>
      </div>
    </section>
  );
}
